import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Trophy, Heart, Calendar, MapPin, Crown, Share2, ArrowUpRight, Copy } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import api from '../lib/api';
import { formatEventDate } from '../lib/dateUtils';

export default function BravxProfilePage() {
  const { username: rawUsername } = useParams();
  const username = (rawUsername || '').replace(/^@/, '').toLowerCase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/bravx/profile/${encodeURIComponent(username)}`);
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [username]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `@${data?.username} en Bravo`,
        text: `Apoyalo en tu próxima compra usando @${data?.username}`,
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado al portapapeles');
    }
  };

  const handleCopy = () => {
    if (!data?.username) return;
    navigator.clipboard.writeText('@' + data.username);
    toast.success('@username copiado');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Navbar />
        <div className="py-32 text-center text-white/40">Cargando…</div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div
        className="min-h-screen bg-[#050505] text-white"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <p
            className="text-5xl font-black text-white mb-3 uppercase tracking-tighter"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            Sin resultados
          </p>
          <p className="text-white/50 mb-8">
            No encontramos a <span className="text-[#D4AF37]">@{username}</span> en Bravo.
          </p>
          <Link
            to="/c/top"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-black font-extrabold text-sm uppercase tracking-wider"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            <Trophy size={14} /> Ver ranking
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const initial = (data.display_name || data.username || '?').charAt(0).toUpperCase();

  return (
    <div
      className="min-h-screen bg-[#050505] text-white overflow-x-hidden"
      style={{ fontFamily: "'Manrope', sans-serif" }}
      data-testid="bravx-profile-page"
    >
      <SEO
        title={`@${data.username} · Bravo`}
        description={`${data.display_name || '@' + data.username} acumuló ${data.points} BRAVX points. Ranking #${data.rank || '—'}. Apoyalo usando @${data.username} en tu próxima compra.`}
        url={`/u/${data.username}`}
        image={data.avatar}
      />
      <Navbar />

      {/* Header band */}
      <section className="relative pt-12 sm:pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Liquid-gold ambient bg */}
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://static.prod-images.emergentagent.com/jobs/3e7e6f22-74b0-4df4-a2fb-81cc35d2429a/images/2c7f6f57ab9fadc0bbf9895b79c8e1339189c6d3dab6ebbfa8ca9061a2d0bae6.png')",
            }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6 sm:gap-8">
            {/* Avatar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
              data-testid="bravx-profile-avatar"
            >
              <div
                className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden border-2 border-[#D4AF37]/60 bg-[#0A0A0A] flex items-center justify-center shadow-2xl"
                style={{ boxShadow: '0 12px 60px -10px rgba(212, 175, 55, 0.45)' }}
              >
                {data.avatar ? (
                  <img
                    src={data.avatar}
                    alt={data.username}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span
                    className="text-5xl sm:text-6xl font-black text-[#D4AF37]"
                    style={{ fontFamily: "'Unbounded', sans-serif" }}
                  >
                    {initial}
                  </span>
                )}
              </div>
              {data.is_full_bravx && (
                <span
                  className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 px-2 py-1 rounded-full bg-[#D4AF37] text-black text-[9px] font-black uppercase tracking-wider border-2 border-[#050505]"
                  style={{ fontFamily: "'Unbounded', sans-serif" }}
                >
                  <Crown size={9} /> FULL
                </span>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex-1 min-w-0"
            >
              <p
                className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-2 flex items-center gap-1.5"
                style={{ fontFamily: "'Unbounded', sans-serif" }}
              >
                <Sparkles size={11} /> Bravx Profile
              </p>
              <h1
                className="text-4xl sm:text-6xl font-black leading-[0.95] tracking-tighter break-words"
                style={{ fontFamily: "'Unbounded', sans-serif" }}
                data-testid="bravx-profile-username"
              >
                @{data.username}
              </h1>
              {data.display_name && (
                <p className="mt-2 text-white/60 text-sm sm:text-base truncate">{data.display_name}</p>
              )}
              {data.city && (
                <p className="mt-1 text-white/40 text-xs inline-flex items-center gap-1">
                  <MapPin size={11} /> {data.city}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-5">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold uppercase tracking-wider"
                  data-testid="bravx-profile-copy"
                >
                  <Copy size={12} /> Copiar @
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] hover:bg-[#F5D25A] text-black text-xs font-extrabold uppercase tracking-wider"
                  style={{ fontFamily: "'Unbounded', sans-serif" }}
                  data-testid="bravx-profile-share"
                >
                  <Share2 size={12} /> Compartir perfil
                </button>
                <Link
                  to="/bravx/top"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold uppercase tracking-wider"
                  data-testid="bravx-profile-view-top"
                >
                  <Trophy size={12} /> Ver Top BRAVX
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats bento */}
      <section className="px-4 sm:px-6 lg:px-8 -mt-4 mb-12">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard label="BRAVX Points" value={(data.points || 0).toLocaleString('es-AR')} icon={<Sparkles size={14} />} highlight />
          <StatCard label="Ranking" value={data.rank ? `#${data.rank}` : '—'} icon={<Trophy size={14} />} />
          <StatCard label="Apoyos recibidos" value={(data.referrals_count || 0).toLocaleString('es-AR')} icon={<Heart size={14} />} />
        </div>
      </section>

      {/* Events attended */}
      {data.events_attended && data.events_attended.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 pb-16">
          <div className="max-w-5xl mx-auto">
            <h3
              className="text-xs uppercase tracking-[0.3em] font-bold text-white/40 mb-6 flex items-center gap-2"
              style={{ fontFamily: "'Unbounded', sans-serif" }}
            >
              <Calendar size={12} /> Estuvo en
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bravx-profile-events">
              {data.events_attended.map((ev, idx) => (
                <motion.div
                  key={ev.id}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: idx * 0.04 }}
                >
                  <Link
                    to={`/evento/${ev.slug || ev.id}`}
                    className="group relative block aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-all"
                  >
                    {ev.image_url ? (
                      <div
                        className="absolute inset-0 bg-cover bg-center group-hover:scale-[1.05] transition-transform duration-700"
                        style={{ backgroundImage: `url(${ev.image_url})` }}
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/15 to-transparent" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p
                        className="text-[10px] uppercase tracking-wider font-bold text-[#D4AF37] mb-1"
                        style={{ fontFamily: "'Unbounded', sans-serif" }}
                      >
                        {formatEventDate(ev.date, 'EEE d MMM')}
                      </p>
                      <h4
                        className="text-base sm:text-lg font-extrabold text-white leading-tight tracking-tight uppercase line-clamp-2"
                        style={{ fontFamily: "'Unbounded', sans-serif" }}
                      >
                        {ev.title}
                      </h4>
                      {ev.location && (
                        <p className="text-[11px] text-white/50 mt-1 truncate">{ev.location}</p>
                      )}
                    </div>
                    <span className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/15 group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                      <ArrowUpRight size={13} />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA strip */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div
          className="max-w-5xl mx-auto rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent p-8 sm:p-12 text-center"
          data-testid="bravx-profile-cta"
        >
          <p
            className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-4"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            Apoyá a un Bravx
          </p>
          <h3
            className="text-3xl sm:text-4xl font-black uppercase tracking-tighter leading-tight max-w-2xl mx-auto"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            Usá <span className="text-[#D4AF37]">@{data.username}</span> en tu próxima compra y sumamos puntos los dos.
          </h3>
          <Link
            to={`/eventos?bravx=${data.username}`}
            className="inline-flex items-center gap-3 mt-8 px-6 py-3.5 rounded-full bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-extrabold text-sm uppercase tracking-[0.2em] transition-colors"
            style={{ fontFamily: "'Unbounded', sans-serif" }}
            data-testid="bravx-profile-explore"
          >
            Ver eventos
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function StatCard({ label, value, icon, highlight = false }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${
        highlight
          ? 'bg-[#D4AF37]/[0.06] border-[#D4AF37]/30'
          : 'bg-white/[0.03] border-white/10'
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">
        <span style={{ color: highlight ? '#D4AF37' : 'rgba(255,255,255,0.4)' }}>{icon}</span>
        {label}
      </div>
      <p
        className={`text-4xl sm:text-5xl font-black tracking-tighter leading-none ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}
        style={{ fontFamily: "'Unbounded', sans-serif" }}
      >
        {value}
      </p>
    </div>
  );
}
